'use client';

import { FormEvent, useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { ArrowRightIcon, ChatBubbleLeftRightIcon, ChevronDownIcon, SparklesIcon } from '@heroicons/react/24/outline';

import { Button } from '@/app/Components/Button';
import { OperationalState, OperationalStatus, Surface } from '@/app/Components/OperationalPage';
import RoomLocationLink from '@/app/Components/RoomLocationLink';
import { useCoursePeek } from '@/app/Components/CoursePeek/CoursePeekProvider';
import { markOpenedInPlace } from '@/app/lib/inPlaceNavigation';
import { scheduledSectionContext } from '@/app/Components/CoursePeek/contexts';
import { useCourseWikiPayload } from '@/app/explore/useCourseWikiPayload';
import { findProfessorRatings } from '@/app/lib/professorData';
import { formatMinutes } from '@/app/lib/room-helpers';
import { formatBaseIdDisplay } from '@/app/lib/utils';
import { isRecitationMeeting } from '@/app/lib/meetingTypes';
import { getCampusColor } from '@/app/types/colors';
import type { ScheduledCourse } from '@/app/types/course';
import { readableCourseTitle } from '@/app/lib/courseTitle';

type SirsProfessorComparison = {
  instructor_name: string;
  times_taught: number;
  avg_teaching_score: number | null;
  avg_quality_score: number | null;
  total_enrollment: number;
  total_responses: number;
};

type CourseSirsResponse = { professorStats?: SirsProfessorComparison[] };

function courseInstructorNames(course: ScheduledCourse) {
  return course.instructors
    .map((instructor) => typeof instructor === 'string' ? instructor : instructor.name)
    .filter((name) => Boolean(name) && name !== 'TBA');
}

function instructorNames(course: ScheduledCourse) {
  return courseInstructorNames(course).join(', ') || 'Instructor TBA';
}

function insightId(course: ScheduledCourse) {
  return `schedule-insight-${(course.baseId || course.id).replace(/[^a-zA-Z0-9_-]/g, '-')}`;
}

function humanizeSignalName(name: string) {
  return name.replace(/([A-Z])/g, ' $1').replace(/^./, (character) => character.toUpperCase());
}

function signalTone(score: number): 'critical' | 'warning' | 'info' | 'positive' {
  if (score >= 4) return 'critical';
  if (score >= 3) return 'warning';
  if (score >= 2) return 'info';
  return 'positive';
}

const signalSurfaceTone = {
  critical: 'border-[var(--ss-critical)] bg-[var(--ss-critical-bg)]',
  warning: 'border-[var(--ss-warning)] bg-[var(--ss-warning-bg)]',
  info: 'border-[var(--ss-info)] bg-[var(--ss-info-bg)]',
  positive: 'border-[var(--ss-positive)] bg-[var(--ss-positive-bg)]',
} as const;

function useCourseSirs(course: ScheduledCourse) {
  const [data, setData] = useState<CourseSirsResponse | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    const subject = course.department.replace(/^01:/, '');
    fetch(`/api/sirs/courses/${encodeURIComponent(`${subject}:${course.class_number}`)}`, { signal: controller.signal })
      .then((response) => response.ok ? response.json() : null)
      .then((payload) => setData(payload))
      .catch((error) => {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        setData(null);
      });
    return () => controller.abort();
  }, [course.class_number, course.department]);

  return data;
}

function matchesSelectedProfessor(candidate: string, selectedNames: string[]) {
  const normalized = candidate.toLowerCase();
  return selectedNames.some((name) => {
    const selected = name.toLowerCase();
    return normalized === selected || normalized.includes(selected) || selected.includes(normalized);
  });
}

function CompactBar({ value, max, className }: { value: number; max: number; className: string }) {
  const width = max > 0 ? Math.max((value / max) * 100, 3) : 0;
  return <div className="h-1.5 overflow-hidden rounded-full bg-[var(--ss-surface-subtle)]" aria-hidden="true"><div className={`h-full rounded-full ${className} motion-reduce:transition-none`} style={{ width: `${Math.min(width, 100)}%` }} /></div>;
}

function ProfessorComparison({ data, selectedNames }: { data: CourseSirsResponse | null; selectedNames: string[] }) {
  const [showAll, setShowAll] = useState(false);
  const comparisons = (data?.professorStats || []).filter((entry) => entry.avg_teaching_score !== null);
  const visibleComparisons = showAll ? comparisons : comparisons.slice(0, 5);
  const hiddenCount = Math.max(comparisons.length - 5, 0);
  const maxEnrollment = Math.max(...comparisons.map((entry) => entry.total_enrollment || 0), 1);

  if (!comparisons.length) {
    return <p className="text-sm text-[var(--ss-text-muted)]">No historical professor comparison is available for this course.</p>;
  }

  return (
    <figure aria-label="Historical SIRS professor comparison">
      <figcaption className="text-xs text-[var(--ss-text-muted)]">Historical SIRS results for this course, not live enrollment.</figcaption>
      <div className="mt-3 space-y-3">
        {visibleComparisons.map((professor) => {
          const selected = matchesSelectedProfessor(professor.instructor_name, selectedNames);
          return (
            <div key={professor.instructor_name} className={`rounded-control border p-3 ${selected ? 'border-[var(--ss-focus)] bg-[var(--ss-surface-subtle)]' : 'border-[var(--ss-border)]'}`}>
              <div className="flex items-start justify-between gap-3 text-xs">
                <div className="min-w-0">
                  <p className="truncate font-semibold text-[var(--ss-text)]">{professor.instructor_name}{selected ? ' · This section' : ''}</p>
                  <p className="mt-0.5 text-[var(--ss-text-muted)]">{professor.times_taught} offering{professor.times_taught === 1 ? '' : 's'} · {professor.total_enrollment.toLocaleString()} historical students</p>
                </div>
                <span className="font-semibold tabular-nums text-[var(--ss-text)]">{professor.avg_teaching_score?.toFixed(1)}/5</span>
              </div>
              <div className="mt-2 grid grid-cols-[4rem_minmax(0,1fr)_auto] items-center gap-2 text-[10px] text-[var(--ss-text-muted)]">
                <span>Teaching</span><CompactBar value={professor.avg_teaching_score || 0} max={5} className="bg-[var(--ss-info)]" /><span>{professor.avg_teaching_score?.toFixed(1)}</span>
                <span>Students</span><CompactBar value={professor.total_enrollment || 0} max={maxEnrollment} className="bg-[var(--ss-positive)]" /><span>{professor.total_enrollment.toLocaleString()}</span>
              </div>
            </div>
          );
        })}
      </div>
      {hiddenCount ? (
        <button
          type="button"
          className="mt-3 flex min-h-12 w-full items-center justify-center gap-2 rounded-control border border-[var(--ss-border)] bg-[var(--ss-surface-subtle)] px-3 text-sm font-semibold text-[var(--ss-text)] transition-colors hover:border-[var(--ss-focus)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)] motion-reduce:transition-none md:min-h-10"
          aria-expanded={showAll}
          onClick={() => setShowAll((expanded) => !expanded)}
        >
          {showAll ? 'Show fewer professors' : `Show ${hiddenCount} more professor${hiddenCount === 1 ? '' : 's'}`}
          <ChevronDownIcon className={`h-4 w-4 transition-transform motion-reduce:transition-none ${showAll ? 'rotate-180' : ''}`} aria-hidden="true" />
        </button>
      ) : null}
    </figure>
  );
}

function CourseInsight({ course, onAskAI, defaultOpen = true, semester }: { course: ScheduledCourse; onAskAI: (prompt: string, course?: ScheduledCourse) => void; defaultOpen?: boolean; semester?: string }) {
  const peek = useCoursePeek();
  const guide = useCourseWikiPayload(course.baseId);
  const selectedNames = courseInstructorNames(course);
  const professors = instructorNames(course);
  const sirs = useCourseSirs(course);
  const selectedSirs = sirs?.professorStats?.find((entry) => matchesSelectedProfessor(entry.instructor_name, selectedNames));
  const rmpRatings = useMemo(() => findProfessorRatings(selectedNames), [selectedNames.join('|')]);
  const ratingSignals = guide.wiki?.courseRatings ? Object.entries(guide.wiki.courseRatings) : [];

  return (
    <details id={insightId(course)} className="group overflow-hidden rounded-card border border-[var(--ss-border)] bg-[var(--ss-surface)]" open={defaultOpen}>
      <summary className="relative cursor-pointer list-none p-4 pb-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--ss-focus)]">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h3 className="font-semibold text-[var(--ss-text)]">{readableCourseTitle(course.name)}</h3>
            <p className="mt-1 text-sm text-[var(--ss-text-muted)]">{formatBaseIdDisplay(course.baseId)} · Section {course.section} · {course.credits || '—'} credits</p>
            <p className="mt-1 text-sm text-[var(--ss-text-muted)]">{professors}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`inline-flex rounded-control px-2 py-1 text-xs font-semibold ${getCampusColor(course.campus, 'badge')}`}>{course.campus}</span>
            <ChevronDownIcon className="h-4 w-4 text-[var(--ss-text-muted)] transition-transform group-open:rotate-180 motion-reduce:transition-none" aria-hidden="true" />
          </div>
        </div>
        <span className={`absolute inset-x-0 bottom-0 h-1.5 ${getCampusColor(course.campus, 'solid')}`} aria-hidden="true" />
      </summary>

      {rmpRatings.length || selectedSirs ? (
        <div className="flex flex-wrap gap-2 border-t border-[var(--ss-border)] px-4 py-3">
          {rmpRatings.map((rating) => (
            <a key={rating.id} href={`https://www.ratemyprofessors.com/professor/${rating.id}`} target="_blank" rel="noreferrer" className="inline-flex min-h-9 items-center rounded-control border border-[var(--ss-border)] px-2.5 text-xs font-semibold text-[var(--ss-text)] transition-colors hover:border-[var(--ss-focus)]">
              RMP {rating.avgRating.toFixed(1)}/5 · {rating.numRatings} ratings
            </a>
          ))}
          {selectedSirs?.avg_teaching_score !== null && selectedSirs?.avg_teaching_score !== undefined ? (
            <Link href={`/sirs?professor=${encodeURIComponent(selectedSirs.instructor_name)}`} className="inline-flex min-h-9 items-center rounded-control border border-[var(--ss-border)] px-2.5 text-xs font-semibold text-[var(--ss-text)] transition-colors hover:border-[var(--ss-focus)]">
              SIRS {selectedSirs.avg_teaching_score.toFixed(1)}/5 · {selectedSirs.times_taught} offering{selectedSirs.times_taught === 1 ? '' : 's'}
            </Link>
          ) : null}
        </div>
      ) : null}

      <div className="grid gap-5 border-t border-[var(--ss-border)] p-4 xl:grid-cols-[minmax(0,1.15fr)_minmax(19rem,0.85fr)]">
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-[var(--ss-text)]">Course evidence</h4>
            {guide.state === 'loading' ? <OperationalState kind="loading" title="Loading researched course evidence" /> : null}
            {guide.state === 'error' ? <OperationalState kind="error" title="Course synthesis unavailable" description={guide.error} /> : null}
            {guide.state === 'absent' ? <p className="mt-2 text-sm text-[var(--ss-text-muted)]">No curated Explorer synthesis is available for this course yet.</p> : null}
            {guide.state === 'ready' && guide.wiki ? (
              <div className="mt-3 space-y-3">
                <details className="rounded-control border border-[var(--ss-border)] p-3" open>
                  <summary className="cursor-pointer text-sm font-semibold text-[var(--ss-text)]">Course overview</summary>
                  <p className="mt-2 text-sm text-[var(--ss-text-muted)]">{guide.wiki.officialSummary}</p>
                </details>
                {guide.wiki.studentPerspective?.summary ? (
                  <details className="rounded-control border border-[var(--ss-border)] p-3">
                    <summary className="cursor-pointer text-sm font-semibold text-[var(--ss-text)]">Student-experience synthesis</summary>
                    <p className="mt-2 text-sm text-[var(--ss-text-muted)]">{guide.wiki.studentPerspective.summary}</p>
                  </details>
                ) : null}
                {ratingSignals.length ? (
                  <div>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[var(--ss-text-muted)]">Experience categories</p>
                    <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
                      {ratingSignals.map(([name, signal]) => {
                        if(signal.score==null)return <OperationalStatus key={name}>{humanizeSignalName(name)} · Unrated</OperationalStatus>;
                        const tone = signalTone(signal.score);
                        return (
                          <details key={name} className={`rounded-control border p-2.5 ${signalSurfaceTone[tone]}`}>
                            <summary className="flex cursor-pointer list-none items-center justify-between gap-2">
                              <span className="text-xs font-semibold text-[var(--ss-text)]">{humanizeSignalName(name)}</span>
                              <OperationalStatus tone={tone}>{signal.score}/5</OperationalStatus>
                            </summary>
                            <p className="mt-2 text-xs text-[var(--ss-text-muted)]">{signal.reason}</p>
                            <p className="mt-2 text-[10px] font-semibold uppercase tracking-wide text-[var(--ss-text-muted)]">{signal.confidence} confidence</p>
                          </details>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-2">
            <Link className="ss-button" href={`/explore?course=${encodeURIComponent(course.baseId)}`} onClick={(event) => { if (!peek || event.metaKey || event.ctrlKey) return; event.preventDefault(); markOpenedInPlace(event); peek.openCourse({ courseId: course.baseId, semester, context: scheduledSectionContext(course, 'On this schedule') }); }}>Course details<ArrowRightIcon className="h-4 w-4" /></Link>
            <Button variant="secondary" onClick={() => onAskAI(`What should I know about ${course.name} (${formatBaseIdDisplay(course.baseId)}) with ${professors}?`, course)}>
              <ChatBubbleLeftRightIcon className="h-4 w-4" /> Ask AI about this class
            </Button>
          </div>
        </div>
        <div>
          <h4 className="mb-2 text-sm font-semibold text-[var(--ss-text)]">Professor comparison</h4>
          <ProfessorComparison data={sirs} selectedNames={selectedNames} />
          <div className="mt-4 space-y-2 text-sm text-[var(--ss-text-muted)]">
            {course.meetingTimes.map((meeting, index) => {
              const hasTime = meeting.startMinute > 0 && meeting.endMinute > meeting.startMinute;
              const location = meeting.location || meeting.campus || course.campus;
              return (
                <p key={`${meeting.day}-${index}`}>
                  {isRecitationMeeting(meeting) ? 'Recitation · ' : ''}{meeting.day}{hasTime ? ` · ${formatMinutes(meeting.startMinute)}–${formatMinutes(meeting.endMinute)}` : null}{location ? <> · {meeting.location ? <RoomLocationLink location={meeting.location} className="font-semibold text-[var(--ss-text)]" /> : location}</> : null}
                </p>
              );
            })}
          </div>
        </div>
      </div>
    </details>
  );
}

export default function ScheduleInsightsPanel({ courses, focusedCourseId, onAskAI, defaultCourseOpen = true, semester }: { courses: ScheduledCourse[]; focusedCourseId?: string | null; onAskAI: (prompt: string, course?: ScheduledCourse) => void; defaultCourseOpen?: boolean; semester?: string }) {
  const [question, setQuestion] = useState('');

  useEffect(() => {
    if (!focusedCourseId) return;
    const course = courses.find((entry) => entry.id === focusedCourseId || entry.baseId === focusedCourseId);
    if (!course) return;
    window.requestAnimationFrame(() => document.getElementById(insightId(course))?.scrollIntoView({ block: 'start' }));
  }, [courses, focusedCourseId]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const prompt = question.trim();
    if (!prompt) return;
    onAskAI(prompt);
    setQuestion('');
  };

  return (
    <section className="space-y-4" aria-labelledby="schedule-insights-title">
      {courses.length ? courses.map((course, index) => <CourseInsight key={`${course.id}-${index}`} course={course} onAskAI={onAskAI} defaultOpen={defaultCourseOpen} semester={semester} />) : <OperationalState kind="empty" title="No course information yet" description="Add courses or open a generated schedule to compare course and professor evidence." />}

      <Surface className="p-4">
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(20rem,0.7fr)] lg:items-center">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-[var(--ss-focus)]">Schedule intelligence</p>
            <h2 id="schedule-insights-title" className="mt-1 text-xl font-bold text-[var(--ss-text)]">Course and professor synthesis</h2>
            <p className="mt-2 text-sm text-[var(--ss-text-muted)]">Ask a quick question after comparing the official facts, RMP ratings, and historical SIRS evidence above.</p>
          </div>
          <form onSubmit={submit} className="flex gap-2" aria-label="Ask AI about this schedule">
            <label className="sr-only" htmlFor="schedule-ai-question">Ask a quick question</label>
            <input id="schedule-ai-question" value={question} onChange={(event) => setQuestion(event.target.value)} className="ss-field min-w-0 flex-1" placeholder="Ask about workload, professors, or fit…" />
            <Button type="submit" disabled={!question.trim()}><SparklesIcon className="h-4 w-4" />Ask AI</Button>
          </form>
        </div>
      </Surface>
    </section>
  );
}
